import { SITE_NAME, SITE_TAGLINE } from "./site";

/*
 * 몰 실행 시점 설정 — 스킨 · 브랜드 · 약관 목록 (`docs/base/11-스킨-체계.md` §3 · §5).
 *
 * 🔴 **관리자가 인트라넷에서 바꾸는 값입니다 — 재배포 없이.** `GET /site` 가 정본이고,
 *    몰 루트 `layout.tsx` 가 매 요청 서버에서 받아(`siteConfig.server.ts`) `<html>` 과
 *    컨텍스트(`SiteProvider` · `useSite()`)로 내립니다. 화면은 여기 값을 **읽기만** 합니다.
 *
 * 🔑 **이 파일은 순수 모듈입니다** — `"use client"` 도 `server-only` 도 아닙니다. 서버
 *    로더와 클라이언트 컨텍스트가 **같은 타입·기본값·정규화**를 써야 해서입니다.
 *    (컨텍스트는 `createContext` 가 서버 컴포넌트에서 돌 수 없어 `useSite.tsx` 로 뗐습니다.)
 *
 * 🔴 **기본값은 `App\Support\Skins::defaults()` 와 같아야 합니다** (설계 §2-2 표).
 *    API 가 응답할 때 이미 기본값을 채워 보내므로 이 값이 쓰이는 것은 API 가 없을 때
 *    (첫 기동·장애·`merge main` 직후)뿐이지만, 그때 화면이 «다른 몰» 이 되면 안 됩니다.
 */

/** 이 빌드가 화면을 가진 스킨. 🔑 스킨 폴더(`_skins/<이름>`)를 더하면 여기 한 줄이 늡니다. */
export const SKIN_IDS = ["default", "simple"] as const;
export type SkinId = (typeof SKIN_IDS)[number];

/**
 * 🔴 **화면이 실제로 준비된 스킨만** 활성으로 받아들입니다. 관리자가 아직 화면이 없는
 *    스킨을 고르면(베이스가 먼저 배포되고 화면이 뒤따르는 순서) `default` 로 폴백합니다 —
 *    토큰 없는 `data-skin` 으로 뜨면 화면 전체가 무너집니다.
 *    🔑 `simple` 은 2026-09-23 화면 스무 개가 들어와 더했습니다 (`_skins/registry.tsx` 와 함께).
 */
export const READY_SKINS: readonly SkinId[] = ["default", "simple"];

/**
 * 🆕 서체 레지스트리의 id (2026-09-23 · `docs/base/11` §7-2). 실제 `next/font` 정의는
 * `_skins/fonts.ts` 에 있고, 여기는 **이름만** 둡니다 — 이 파일은 순수 모듈이라 서체 파일을 끌어오지 않습니다.
 * 🔴 인트라넷(`App\Support\Skins`)과 같은 철자여야 합니다 — 모르는 id 는 스킨 기본 서체로 떨어집니다.
 */
export const FONT_IDS = ["gothic-a1", "noto-sans-kr", "ibm-plex-sans-kr", "nanum-gothic", "gowun-dodum", "system"] as const;
export type FontId = (typeof FONT_IDS)[number];

/** 🆕 본문 글자 크기(px)의 범위 — 🔴 밖이면 기본값입니다(잘라 넣지 않습니다 · API 와 같음). */
export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 18;

/**
 * 🆕 글자 설정 — **두 스킨 공통** (2026-09-23 오너 검수 2차 · `docs/base/11` §7-2).
 * 🔑 브랜드색과 글자색이 같으면 디자인이 무너져서 글자색을 따로 받습니다.
 */
export type TypographySettings = {
  /** 본문 글자색 (`--tnf-ink`) — 보조·흐린 글자(`--tnf-ink-soft`·`-faint`)는 스킨 `tokens.css` 가 여기서 우려냅니다 */
  textColor: string;
  /** 본문 서체 — `_skins/fonts.ts` 의 id */
  fontFamily: FontId;
  /** 본문 기준 글자 크기(px) — 스킨의 글자 계단(`--tnf-text-*`)이 전부 이 값의 배수입니다 (`--tnf-text-base`) */
  fontSize: number;
};

export type DefaultSkinSettings = TypographySettings & {
  /** 헤더 배경이 되는 브랜드색 (`--tnf-accent`) */
  brandColor: string;
};

/**
 * 🆕 simple 의 문구 (2026-09-28 · `docs/base/12` §3) — 관리자가 인트라넷 «몰 설정» 에서 바꿉니다.
 * 🔴 기본값은 `App\Support\Skins` 의 `copy_*` 기본값(= 화면의 지금 문장)과 같아야 합니다.
 */
export type SimpleCopy = {
  copyHomeTitle: string;
  copyRecipientLabel: string;
  copyBuySuffix: string;
  /** 두 줄까지 — 줄바꿈(`\n`)이 뜻입니다 */
  copyLandingTitle: string;
  copyLandingLead: string;
  copyLandingProducts: string;
};

export type SimpleSkinSettings = TypographySettings & SimpleCopy & {
  brandColor: string;
  /** 모서리 기준값(px) 0~28 — 토큰 전부가 이 값의 배수입니다 (`--tnf-round`) */
  round: number;
  /** PC 폭에서 `/` 를 랜딩으로 보여 줄지 */
  pcLanding: boolean;
  /** 랜딩 히어로 이미지 — `mediaSrc()` 를 거쳐 씁니다. 없으면 몰의 기본 이미지 */
  heroUrl: string | null;
};

export type LegalKind = "terms" | "privacy" | "youth" | (string & {});

/** 본문이 채워진 약관만 옵니다 — 빈 것은 링크조차 그리지 않습니다 (설계 §2-3). */
export type LegalEntry = { kind: LegalKind; title: string; effectiveOn: string | null };

export type SiteBrand = {
  siteName: string;
  tagline: string;
  /** API 상대 경로(`/site/images/logo?v=…`). `mediaSrc()` 로 BFF 주소가 됩니다. 없으면 `BrandMark` */
  logoUrl: string | null;
  /**
   * 🆕 API 상대 경로(`/site/images/favicon?v=…`). 없으면 null — 몰의 기본 파비콘(`public/icon.svg` ·
   * `public/apple-icon.png`). `layout.tsx` 의 `generateMetadata().icons` 가 `mediaSrc()` 로 씁니다.
   */
  faviconUrl: string | null;
};

/* ── 🆕 블록 (2026-09-28 · `docs/base/12` §6) ─────────────────────────────────
 * 관리자가 인트라넷 «화면 구성» 에서 정해진 자리(zone)에 넣는 것. 봉투(`GET /site`)에 **게시된·지금 보이는**
 * 것만 실려 옵니다(켜짐·기간은 서버가 이미 걸렀습니다). 🔴 모양은 서버 `App\Support\Blocks::publicList` 와 같습니다.
 */
export type BlockLink = { link: string | null; tooltip: string | null; external: boolean };
export type BannerSlide = BlockLink & { imageUrl: string; alt: string };

export type SiteBlock =
  | { id: string; zone: string; type: "banner"; data: { ratio: "3:1" | "2:1" | "1:1"; slides: BannerSlide[] } }
  | { id: string; zone: string; type: "notice"; data: BlockLink & { text: string; tone: "plain" | "accent" } }
  | { id: string; zone: string; type: "text"; data: { title: string | null; bodyHtml: string } }
  | { id: string; zone: string; type: "faq"; data: { title: string; items: { q: string; a: string }[] } };

type SiteCommon = { brand: SiteBrand; legal: LegalEntry[]; blocks: SiteBlock[]; version: string };

export type SiteConfig =
  | (SiteCommon & { skin: "default"; settings: DefaultSkinSettings })
  | (SiteCommon & { skin: "simple"; settings: SimpleSkinSettings });

export const ROUND_MIN = 0;
export const ROUND_MAX = 28;

export const DEFAULT_SETTINGS: { default: DefaultSkinSettings; simple: SimpleSkinSettings } = {
  /* 🔴 `textColor`·`fontSize` 는 각 스킨 `tokens.css` 의 `--tnf-ink`·본문 크기와 같아야 합니다 — 기본값에서 픽셀이 그대로여야 합니다. */
  default: { brandColor: "#101319", textColor: "#101319", fontFamily: "gothic-a1", fontSize: 13 },
  simple: {
    brandColor: "#1b64da",
    textColor: "#191f28",
    fontFamily: "noto-sans-kr",
    fontSize: 14,
    round: 20,
    pcLanding: false,
    heroUrl: null,
    copyHomeTitle: "상품권 보내기",
    copyRecipientLabel: "받는 사람",
    copyBuySuffix: "구매하기",
    copyLandingTitle: "고르고, 번호 넣고,\n보냅니다",
    copyLandingLead: "결제하면 핀번호가 바로 발급됩니다.",
    copyLandingProducts: "취급 상품권",
  },
};

/** 🆕 문구 칸 이름 — 미리보기 쿼리도 같은 이름입니다(인트라넷이 `Str::camel` 로 만듭니다). */
export const SIMPLE_COPY_KEYS = [
  "copyHomeTitle",
  "copyRecipientLabel",
  "copyBuySuffix",
  "copyLandingTitle",
  "copyLandingLead",
  "copyLandingProducts",
] as const satisfies readonly (keyof SimpleCopy)[];

/** 문구 한 칸의 길이 상한 — 🔑 서버가 이미 거른 값이지만 미리보기 쿼리는 아무 값이나 올 수 있어 한 번 더 자릅니다. */
const COPY_MAX = 60;

export const DEFAULT_BRAND: SiteBrand = { siteName: SITE_NAME, tagline: SITE_TAGLINE, logoUrl: null, faviconUrl: null };

/** API 를 한 번도 못 받았을 때의 설정 — 지금 몰 그대로(`default`)입니다. */
export const DEFAULT_SITE_CONFIG: SiteConfig = {
  skin: "default",
  brand: DEFAULT_BRAND,
  settings: DEFAULT_SETTINGS.default,
  legal: [],
  blocks: [],
  version: "default",
};

/* ── 정규화 ────────────────────────────────────────────────────────────────
 * 🔴 **API 응답을 믿지 않고 한 번 더 좁힙니다.** 값은 `<html style>` 에 그대로 박히고
 *    (브랜드색·모서리) 스킨 이름은 화면 코드를 고릅니다 — 모양이 틀린 값 하나가 몰
 *    전체를 무너뜨리는 자리입니다. 틀린 칸은 **그 칸만** 기본값으로 돌립니다.
 *
 * 🔑 키는 snake_case·camelCase 둘 다 받습니다. API 응답은 관례상 camelCase 로 바뀌어
 *    오지만(`docs/base/07` §7-1), 설계 문서(§3)의 봉투는 snake_case 로 적혀 있습니다.
 */
type Loose = Record<string, unknown>;

function pick(obj: Loose, camel: string, snake: string): unknown {
  return obj[camel] !== undefined ? obj[camel] : obj[snake];
}

function asObject(value: unknown): Loose {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Loose) : {};
}

function asText(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : fallback;
}

function asNullableText(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** `#rrggbb` 만 받습니다 — `<html style>` 에 들어가는 값이라 그 밖의 문법을 통과시키지 않습니다. */
export function asColor(value: unknown, fallback: string): string {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim().toLowerCase() : fallback;
}

export function asRound(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(ROUND_MAX, Math.max(ROUND_MIN, Math.round(n)));
}

function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
}

export function isFontId(value: unknown): value is FontId {
  return typeof value === "string" && (FONT_IDS as readonly string[]).includes(value);
}

/** 본문 글자 크기 — 🔴 정수이고 범위 안일 때만. 그 밖(소수·범위 밖·모양 틀림)은 기본값입니다. */
export function asFontSize(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
  return Number.isInteger(n) && n >= FONT_SIZE_MIN && n <= FONT_SIZE_MAX ? n : fallback;
}

/**
 * 글자 설정 세 칸 — 🔑 API 가 이 칸을 아직 안 보내는 판본(글자 설정 이전)이면 스킨 기본값입니다.
 * 키는 다른 칸처럼 camelCase·snake_case 둘 다 받습니다.
 */
function typography(raw: Loose, d: TypographySettings): TypographySettings {
  const font = pick(raw, "fontFamily", "font_family");
  return {
    textColor: asColor(pick(raw, "textColor", "text_color"), d.textColor),
    fontFamily: isFontId(font) ? font : d.fontFamily,
    fontSize: asFontSize(pick(raw, "fontSize", "font_size"), d.fontSize),
  };
}

/**
 * 🆕 문구 — 🔄 **빈 문자열은 그대로 둡니다 = 그 자리를 뺀다** (2026-09-28 오너 지시). 칸이 아예 없거나 문자열이 아닐
 * 때(API 가 이 칸을 모르는 판본 · 첫 기동)만 기본 문구입니다. 줄은 두 줄까지(랜딩 큰 제목).
 */
function copy(raw: Loose, d: SimpleCopy): SimpleCopy {
  const out = {} as SimpleCopy;
  for (const key of SIMPLE_COPY_KEYS) {
    const value = raw[key];
    out[key] =
      typeof value === "string"
        ? value.replace(/\r\n?/g, "\n").trim().split("\n").slice(0, 2).join("\n").slice(0, COPY_MAX)
        : d[key];
  }
  return out;
}

function asLink(raw: Loose): BlockLink {
  const link = typeof raw.link === "string" ? raw.link.trim() : "";
  /* 🔴 서버와 같은 두 모양만 — 몰 안 경로(`//` 제외) 또는 https. 그 밖은 링크 없음으로 그립니다. */
  const ok = /^\/(?!\/)/.test(link) || /^https:\/\/[^\s]+$/.test(link);
  const tooltip = asNullableText(raw.tooltip);
  return ok ? { link, tooltip: tooltip ?? "열기", external: link.startsWith("https://") } : { link: null, tooltip: null, external: false };
}

/**
 * 🆕 블록 목록 — 🔴 **모르는 종류·모양이 틀린 블록은 버립니다**(인트라넷이 먼저 배포되고 몰이 늦어도 몰이 깨지지 않게).
 * 이미 정규화된 값(미리보기가 다시 넣는 것)도 그대로 통과합니다.
 */
export function normalizeBlocks(raw: unknown): SiteBlock[] {
  const out: SiteBlock[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const b = asObject(item);
    const d = asObject(b.data);
    const id = typeof b.id === "string" ? b.id : "";
    const zone = typeof b.zone === "string" ? b.zone : "";
    if (!id || !zone) continue;

    if (b.type === "banner") {
      const slides = (Array.isArray(d.slides) ? d.slides : [])
        .map(asObject)
        .filter((s) => typeof s.imageUrl === "string" && s.imageUrl !== "")
        .map((s) => ({ ...asLink(s), imageUrl: String(s.imageUrl), alt: typeof s.alt === "string" ? s.alt : "" }));
      const ratio = d.ratio === "2:1" || d.ratio === "1:1" ? d.ratio : "3:1";
      if (slides.length > 0) out.push({ id, zone, type: "banner", data: { ratio, slides } });
    } else if (b.type === "notice") {
      const text = asNullableText(d.text);
      if (text) out.push({ id, zone, type: "notice", data: { ...asLink(d), text, tone: d.tone === "accent" ? "accent" : "plain" } });
    } else if (b.type === "text") {
      const bodyHtml = typeof d.bodyHtml === "string" ? d.bodyHtml : "";
      if (bodyHtml.trim() !== "") out.push({ id, zone, type: "text", data: { title: asNullableText(d.title), bodyHtml } });
    } else if (b.type === "faq") {
      const items = (Array.isArray(d.items) ? d.items : [])
        .map(asObject)
        .filter((it) => typeof it.q === "string" && typeof it.a === "string" && it.q !== "" && it.a !== "")
        .map((it) => ({ q: String(it.q), a: String(it.a) }));
      /* 🔄 제목이 빈 문자열이면 제목 줄 없이(관리자가 비움) · 칸이 없으면 기본 제목. */
      const title = typeof d.title === "string" ? d.title.trim() : "자주 묻는 질문";
      if (items.length > 0) out.push({ id, zone, type: "faq", data: { title, items } });
    }
  }
  return out;
}

export function isSkinId(value: unknown): value is SkinId {
  return typeof value === "string" && (SKIN_IDS as readonly string[]).includes(value);
}

/**
 * `GET /site` 의 `data` 를 `SiteConfig` 로.
 *
 * 🔴 **화면이 없는 스킨은 `default` 로 떨어집니다**(`READY_SKINS`). 그때 `settings` 는
 *    `default` 의 기본값입니다 — API 는 활성 스킨의 설정만 보내므로 `default` 의 값은
 *    몰이 알 길이 없습니다(설계 §3 «활성 스킨의 것만»).
 */
export function normalizeSiteConfig(raw: unknown): SiteConfig {
  const data = asObject(raw);
  const brandRaw = asObject(data.brand);
  const settingsRaw = asObject(data.settings);

  const brand: SiteBrand = {
    siteName: asText(pick(brandRaw, "siteName", "site_name"), DEFAULT_BRAND.siteName),
    tagline: asText(brandRaw.tagline, DEFAULT_BRAND.tagline),
    logoUrl: asNullableText(pick(brandRaw, "logoUrl", "logo_url")),
    /* 🔑 API 가 이 칸을 아직 안 보내는 판본(파비콘 업로드 이전)이면 그냥 null — 기본 파비콘입니다. */
    faviconUrl: asNullableText(pick(brandRaw, "faviconUrl", "favicon_url")),
  };

  const legal: LegalEntry[] = (Array.isArray(data.legal) ? data.legal : [])
    .map(asObject)
    .filter((entry) => typeof entry.kind === "string" && entry.kind !== "")
    .map((entry) => ({
      kind: entry.kind as LegalKind,
      title: asText(entry.title, String(entry.kind)),
      effectiveOn: asNullableText(pick(entry, "effectiveOn", "effective_on")),
    }));

  const blocks = normalizeBlocks(data.blocks);
  const version = asText(data.version, "unknown");
  const requested = isSkinId(data.skin) ? data.skin : "default";
  const skin: SkinId = READY_SKINS.includes(requested) ? requested : "default";

  if (skin === "simple") {
    const d = DEFAULT_SETTINGS.simple;
    return {
      skin,
      brand,
      legal,
      blocks,
      version,
      settings: {
        ...typography(settingsRaw, d),
        ...copy(settingsRaw, d),
        brandColor: asColor(pick(settingsRaw, "brandColor", "brand_color"), d.brandColor),
        round: asRound(settingsRaw.round, d.round),
        pcLanding: asBool(pick(settingsRaw, "pcLanding", "pc_landing"), d.pcLanding),
        heroUrl: asNullableText(pick(settingsRaw, "heroUrl", "hero_url")),
      },
    };
  }

  const d = DEFAULT_SETTINGS.default;
  return {
    skin: "default",
    brand,
    legal,
    /* 🔑 `default` 에는 아직 자리가 없습니다 — 서버도 빈 목록을 보내지만, 폴백일 때 다른 스킨의 블록을 옮기지 않습니다. */
    blocks: requested === "default" ? blocks : [],
    version,
    /* 🔑 요청한 스킨이 폴백된 것이면 그 스킨의 값(색·글자)을 `default` 에 옮기지 않습니다. */
    settings:
      requested === "default"
        ? { ...typography(settingsRaw, d), brandColor: asColor(pick(settingsRaw, "brandColor", "brand_color"), d.brandColor) }
        : d,
  };
}

/**
 * 브랜드색 위에 올릴 글자색 — 밝은 색이면 먹색, 아니면 흰색.
 *
 * 🔴 **관리자가 어떤 색을 올려도 버튼 글자가 읽혀야 합니다** (2026-09-23 검증에서 잡힌 자리).
 *    노랑·연두 같은 밝은 브랜드색에 흰 글자를 얹으면 대비가 2:1 아래로 떨어져 버튼 문구가
 *    사라집니다. 스킨의 `--tnf-on-accent` 기본값(흰색)을 여기서 계산한 값이 덮습니다.
 *    `default` 의 헤더 글자·선·채움도 이 값에서 우려내므로(`tokens.css`) 같이 따라갑니다.
 *
 * 🔑 WCAG 상대 휘도로 판정합니다. 흰 글자의 대비가 3:1 을 넘기는 경계(휘도 ≈ 0.4)를 씁니다 —
 *    큰 글자·굵은 글자의 최소 기준(AA Large)입니다.
 */
export function onAccentFor(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#ffffff";
  const channel = (i: number) => {
    const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.4 ? "#191f28" : "#ffffff";
}

/**
 * `<html style>` 에 박을 CSS 변수. 🔑 스킨이 쓰지 않는 변수는 싣지 않습니다.
 *
 * 🆕 글자색·글자 크기(2026-09-23)도 여기서 나갑니다. 서체는 변수가 아니라 `<body>` 의 클래스라
 *    `_skins/meta.ts` 의 `bodyClassName()` 이 맡습니다 — `next/font` 가 서체 파일을 클래스에 묶어서입니다.
 */
export function siteStyleVars(site: SiteConfig): Record<string, string> {
  const vars: Record<string, string> = {
    "--tnf-accent": site.settings.brandColor,
    "--tnf-on-accent": onAccentFor(site.settings.brandColor),
    "--tnf-ink": site.settings.textColor,
    "--tnf-text-base": `${site.settings.fontSize}px`,
  };
  if (site.skin === "simple") vars["--tnf-round"] = `${site.settings.round}px`;
  return vars;
}
